import { useState, useRef, useEffect } from "react";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";

interface SlideToLogoutProps {
  onLogout: () => void;
  className?: string;
}

export const SlideToLogout = ({ onLogout, className }: SlideToLogoutProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMove = (clientX: number) => {
    if (!isDragging || !trackRef.current || !buttonRef.current) return;

    const track = trackRef.current;
    const button = buttonRef.current;
    const trackRect = track.getBoundingClientRect();
    const buttonWidth = button.offsetWidth;
    const maxPosition = trackRect.width - buttonWidth;

    let newPosition = clientX - trackRect.left - buttonWidth / 2;
    newPosition = Math.max(0, Math.min(newPosition, maxPosition));

    setPosition(newPosition);

    // Trigger logout when slider reaches the end
    if (newPosition >= maxPosition * 0.95) {
      setIsDragging(false);
      onLogout();
    }
  };

  const handleEnd = () => {
    setIsDragging(false);
    // Reset position if not completed
    setPosition(0);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const handleTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX);

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("mouseup", handleEnd);
      document.addEventListener("touchend", handleEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging]);

  return (
    <div className={cn("relative w-full", className)}>
      <div
        ref={trackRef}
        className="relative h-12 bg-gradient-to-r from-destructive/10 to-destructive/20 rounded-full border border-destructive/20 overflow-hidden"
      >
        {/* Progress fill */}
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-destructive/20 to-destructive/30 transition-all duration-100"
          style={{ width: position + 48 }}
        />

        {/* Text label */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-sm font-medium text-destructive/60">
            Slide to logout
          </span>
        </div>

        {/* Draggable button */}
        <div
          ref={buttonRef}
          className="absolute top-1 left-1 bottom-1 w-10 bg-gradient-to-br from-destructive/80 to-destructive rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg transition-shadow hover:shadow-xl"
          style={{ transform: `translateX(${position}px)` }}
          onMouseDown={handleStart}
          onTouchStart={handleStart}
        >
          <LogOut className="w-4 h-4 text-destructive-foreground" />
        </div>
      </div>
    </div>
  );
};
